import { useEffect, useRef, type MutableRefObject } from "react";

/**
 * Jump the window to the top, ignoring the site-wide `scroll-behavior: smooth`.
 *
 * Opening a new screen should feel like opening a new page: the reader starts
 * at the top, not wherever they had scrolled to on the previous screen. A
 * smooth animation would instead show the old content sliding past, so this
 * always snaps.
 */
export function scrollWindowToTop(): void {
  if (typeof window === "undefined") return;
  // "instant" is the spec value; older TS lib typings only know "auto"/"smooth".
  window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
}

/**
 * Scroll to the top whenever `key` changes after mount.
 *
 * Use this on screens that swap their content with React state rather than a
 * route or `?tab=` change (tenant dashboard tabs, referral hub → list, owner
 * property tabs). Route- and `?tab=`-driven screens are already covered by
 * `<ScrollToTop />` in App.tsx. The mount itself is skipped so a panel that
 * appears mid-page does not yank the reader away from where they were.
 *
 * `skipNextRef`, if given, lets a caller suppress a single upcoming reset —
 * set it to `true` right before changing `key` when that same change already
 * has its own explicit scroll destination (e.g. a "view all" link that jumps
 * to a specific section instead of the top). The hook clears it back to
 * `false` once consumed, whether or not it fired.
 */
export function useScrollToTopOnChange(
  key: unknown,
  skipNextRef?: MutableRefObject<boolean>
): void {
  const previous = useRef(key);
  useEffect(() => {
    if (Object.is(previous.current, key)) return;
    previous.current = key;
    if (skipNextRef?.current) {
      skipNextRef.current = false;
      return;
    }
    scrollWindowToTop();
  }, [key, skipNextRef]);
}
